import { ExpandableForm } from 'components/misc/ExpandableForm';
import { useCustomerReferencesHook } from 'hooks/skills/useCustomerReferences';
import { useGenerateSlidesHook } from 'hooks/skills/useGenerateSlides';
import { Button, Checkbox, Input, Panel } from 'rsuite';
import { useMemo, useState } from 'react';

import { showErrorNotification, showNotification } from '../ShowNotification';
import CustomerReferencesList from './CustomerReferencesList';
import CustomersWithoutReferencesList from './CustomersWithoutReferencesList';
import { CustomerReferenceAddForm } from './skills/CustomerReferenceAddForm';

const CustomerReferencesScreen = () => {
    const {
        customerReferences,
        customersWithoutReference,
        isLoading,
        refetch: refetchReferences,
    } = useCustomerReferencesHook();
    const { generateSlides, isGenerateSlidesLoading } = useGenerateSlidesHook();

    const [customerSearchWord, setCustomerSearchWord] = useState('');
    const [notesSearchWord, setNotesSearchWord] = useState('');
    const [showCustomersWithoutReference, setShowCustomersWithoutReference] = useState(false);

    const editAllowed = true;

    const onGenerateSlides = () => {
        generateSlides(filteredCustomerReferences, {
            onSuccess: filename => {
                showNotification({
                    type: 'success',
                    header: 'Creating Customer references',
                    duration: 15000,
                    body: `Creating new customer references presentation: ${filename}`,
                });
            },
            onError: err => {
                showErrorNotification(err);
            },
        });
    };

    const filteredCustomerReferences = useMemo(() => {
        if (!customerSearchWord.trim() && !notesSearchWord.trim()) {
            return customerReferences;
        }

        const customerSearchTerm = customerSearchWord.toLowerCase().trim();
        let filteredReferences = customerReferences.filter(reference =>
            reference.customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()),
        );
        const notesSearchTerm = notesSearchWord.toLowerCase().trim();
        filteredReferences = filteredReferences.filter(reference =>
            reference.reference.notes?.toLowerCase().includes(notesSearchTerm.toLowerCase()),
        );
        return filteredReferences;
    }, [customerReferences, customerSearchWord, notesSearchWord]);

    const filteredCustomersWithoutReference = useMemo(() => {
        if (!customerSearchWord.trim() && !notesSearchWord.trim()) {
            return customersWithoutReference;
        }

        const customerSearchTerm = customerSearchWord.toLowerCase().trim();
        let filteredReferences = customersWithoutReference?.filter(reference =>
            reference.customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()),
        );
        const notesSearchTerm = notesSearchWord.toLowerCase().trim();
        filteredReferences = filteredReferences?.filter(reference =>
            reference.reference.notes?.toLowerCase().includes(notesSearchTerm.toLowerCase()),
        );
        return filteredReferences;
    }, [customersWithoutReference, customerSearchWord, notesSearchWord]);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, gap: '8px' }}>
                <Button
                    appearance="primary"
                    color="green"
                    disabled={!customerReferences}
                    loading={isGenerateSlidesLoading}
                    onClick={onGenerateSlides}
                >
                    Generate Slides
                </Button>
                <span>
                    Generated presentations can be found in Google Drive folder{' '}
                    <a
                        href="https://drive.google.com/drive/folders/1Rx_-_-3Nz5ENzKCeBBJ2nPZmM42VvfRS"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </a>
                    .
                </span>
            </div>
            <ExpandableForm
                formTitle="Add Customer Reference"
                style={{ maxWidth: 800, width: '100%' }}
                render={done => (
                    <CustomerReferenceAddForm
                        onAdded={done}
                        refetchReferences={refetchReferences}
                    />
                )}
            />

            <Panel
                bordered
                style={{ marginBottom: 10, marginTop: 10 }}
                header={<h3>Customer References</h3>}
            >
                <Panel>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div>Search: </div>
                        <Input
                            placeholder="Search by customer name"
                            value={customerSearchWord}
                            onChange={setCustomerSearchWord}
                            style={{
                                marginBottom: 10,
                                paddingTop: 10,
                                maxWidth: 300,
                                width: '100%',
                            }}
                        />
                        <Input
                            placeholder="Search by speaker notes"
                            value={notesSearchWord}
                            onChange={setNotesSearchWord}
                            style={{
                                marginBottom: 10,
                                paddingTop: 10,
                                maxWidth: 300,
                                width: '100%',
                            }}
                        />
                    </div>
                    <Checkbox
                        checked={showCustomersWithoutReference}
                        onChange={(value, checked) => setShowCustomersWithoutReference(checked)}
                    >
                        Show customers without references ({customersWithoutReference?.length})
                    </Checkbox>
                </Panel>
                {showCustomersWithoutReference ? (
                    <CustomersWithoutReferencesList
                        customersWithoutReference={filteredCustomersWithoutReference}
                        editAllowed={editAllowed}
                    />
                ) : (
                    <CustomerReferencesList
                        isLoading={isLoading}
                        filteredCustomerReferences={filteredCustomerReferences}
                        customerSearchWord={customerSearchWord}
                        notesSearchWord={notesSearchWord}
                        editAllowed={editAllowed}
                    />
                )}
            </Panel>
        </div>
    );
};

export default CustomerReferencesScreen;
